// The React adapter over the resource registry — one hook per read, and
// nothing else.
//
// A hook here does exactly two things: call its resource and hand the result to
// `useQuery`. Every decision that used to live in this file — the key, the
// staleTime, the retry shape, the poll interval, the envelope unwrap — is now a
// field on the resource in [resources/](./resources/), which is what lets a
// route loader warm the very same slot the hook reads. When a hook needs
// something no resource field expresses, that is the signal to add the field,
// not to open a literal options object here.
//
// The mutations are the exception and stay: they are genuinely React-shaped,
// and they invalidate through `resource.all` / `resource.key(…)` so a renamed
// key cannot silently turn a write's refresh into a no-op.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useRegisteredMutation } from './mutation';
import { buildQs } from './query-string';
// The query-param shapes live with the resources that consume them; re-exported
// here because ~20 screens import them from this barrel.
export type {
  AudiencesQuery, CampaignsQuery, CreativesQuery, EventsQuery,
  PixelsQuery, ProductionsQuery, VenuesQuery,
} from './resources';
export type { AdminUsersQuery, AuditQuery, LogFilters } from './resources';
import type { AdminUsersQuery, AuditQuery, LogFilters } from './resources';
import type {
  AudiencesQuery, CampaignsQuery, CreativesQuery, EventsQuery,
  PixelsQuery, ProductionsQuery, VenuesQuery,
} from './resources';
import { CADENCE } from './resource';
export {
  reportingApiQuery,
} from './resources';
export type {
  DeliveryQuery, MarkupQuery, ReportDateQuery, ReportingQuery, SpilloverQuery,
} from './resources';
import type {
  DeliveryQuery, MarkupQuery, ReportingQuery, SpilloverQuery,
} from './resources';
import { campaignOverview, deliveryPlacements, markupByCampaign, markupByChannel, markupByProduction, markupSummary, reportingByAudience, reportingByAudienceType, reportingByCampaign, reportingByChannel, reportingByCreative, reportingByProduction, reportingQuery, reportingQueryAllPages, reportingSummary, spilloverFlows, spilloverSummary, spilloverTopHalo } from './resources';
import { copilotStatus, flowFeed, flowHistoryConversions, flowHistoryTickets, flowHistoryWebEvents, flowLineage, flowSnapshot, flowTicketsRecent } from './resources';
import { adminAlertById, adminAlertRules, adminAlerts, adminAudit, adminAuditRecent, adminLogArchive, adminLogArchiveObject, adminLogGroups, adminLogStats, adminLogs, adminNotificationChannels, adminRoles, adminUserById, adminUsers } from './resources';
import { audienceById, audiencesList, campaignById, campaignsList, creativeById, creativesList, eventsPace, pixelById, pixelDefaults, pixelsList } from './resources';
import { conversionsHealth, healthMetrics, healthSummary, pipelineCorrections, pipelinePolicy, pipelineRuns, pipelineStatDetail, pipelineStats, pixelActivity } from './resources';
import { eventsList, productionById, productionsList, productionsRunningSummary, venueById, venuesList } from './resources';
import { feedAfterPage, isEventType } from '@/lib/flow-masks';
import { fmtDateInput } from '@/lib/datetime';
import type {
  AdminUserResponse,
  AudienceResponse,
  AudiencesListResponse,
  AuditListResponse,
  IngestRun,
  IngestCorrection,
  ProductionStat,
  ProductionStatDay,
  CampaignResponse,
  CampaignOverviewRequest,
  CampaignsListResponse,
  CreativeResponse,
  CreativesListResponse,
  DeliveryPlacementsResponse,
  EventsListResponse,
  LogsResponse,
  MarkupByCampaignResponse,
  MarkupByChannelResponse,
  MarkupByProductionResponse,
  MarkupSummaryResponse,
  PixelActivityResponse,
  PerfHaloExportRequest,
  PixelResponse,
  PixelsListResponse,
  ProductionResponse,
  ProductionsListResponse,
  RunningProductionsSummaryResponse,
  ReportByAudienceResponse,
  ReportByAudienceTypeResponse,
  ReportByCampaignResponse,
  ReportByChannelResponse,
  ReportByCreativeResponse,
  ReportByProductionResponse,
  ReportQueryRequest,
  ReportQueryResponse,
  ReportSummaryResponse,
  RolesResponse,
  SpilloverFlowsResponse,
  SpilloverSummaryResponse,
  SpilloverTopHaloResponse,
  UsersListResponse,
  VenueResponse,
  VenuesListResponse,
  LogStats,
  LogGroupsPage,
  AlertsResponse,
  LogArchiveResponse,
  LogsPage,
  AlertRulesResponse,
  HealthSummary,
  ConversionsHealthResponse,
  EventsPaceResponse,
  ConversionsHistoryResponse,
  PipelineMonitorPolicy,
  FlowEventRedacted,
  FlowLivePage,
  FlowRevealResponse,
  PipelineFlowSnapshot,
  TicketsHistoryResponse,
  WebEventsHistoryResponse,
} from './types';
import type {
  NotificationChannelsResponse,
  SystemHealthResponse,
  ExplainRequest,
  ExplainResponse,
  PixelDefaults,
  RegenerateStarted,
  TicketFlowPage,
  TicketRevealResponse,
} from './types.gen';
import type { CopilotStatus, PipelineLineageResponse } from './types';

// ── Productions ────────────────────────────────────────────────────────────

export function useProductions(q: ProductionsQuery = {}) {
  return useQuery(productionsList(q));
}

export function useProduction(id: string) {
  return useQuery(productionById(id, { enabled: !!id }));
}

export function useRunningProductionsSummary() {
  return useQuery(productionsRunningSummary(undefined));
}

// ── Campaigns ──────────────────────────────────────────────────────────────

export function useCampaigns(q: CampaignsQuery = {}) {
  return useQuery(campaignsList(q));
}

export function useCampaign(id: string) {
  return useQuery(campaignById(id, { enabled: !!id }));
}

export function useCampaignOverview(
  request: CampaignOverviewRequest,
  options: { enabled?: boolean } = {},
) {
  return useQuery(campaignOverview(request, options));
}

export function useCampaignOverviewExport() {
  return useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (request: CampaignOverviewRequest) =>
      api.post<string>('/campaigns/overview-export', { body: request }),
    retry: 0,
  });
}

// ── Audiences ──────────────────────────────────────────────────────────────

export function useAudiences(q: AudiencesQuery = {}) {
  return useQuery(audiencesList(q));
}

export function useAudience(id: string) {
  return useQuery(audienceById(id, { enabled: !!id }));
}

// ── Creatives ──────────────────────────────────────────────────────────────

export function useCreatives(q: CreativesQuery = {}) {
  return useQuery(creativesList(q));
}

export function useCreative(id: string) {
  return useQuery(creativeById(id, { enabled: !!id }));
}

// ── Events ─────────────────────────────────────────────────────────────────

export function useEvents(q: EventsQuery = {}) {
  return useQuery(eventsList(q));
}

export function useEventsPace() {
  return useQuery(eventsPace(undefined));
}

// ── Venues ─────────────────────────────────────────────────────────────────

export function useVenues(q: VenuesQuery = {}) {
  return useQuery(venuesList(q));
}

export function useVenue(id: string) {
  return useQuery(venueById(id, { enabled: !!id }));
}

// ── Pixels ─────────────────────────────────────────────────────────────────

export function usePixels(q: PixelsQuery = {}) {
  return useQuery(pixelsList(q));
}

export function usePixelDefaults() {
  return useQuery(pixelDefaults(undefined));
}

export function usePixel(id: string) {
  return useQuery(pixelById(id, { enabled: !!id }));
}

// ── Reporting ──────────────────────────────────────────────────────────────

export function useReportingSummary(q: ReportingQuery = {}, enabled = true) {
  return useQuery(reportingSummary(q, { enabled }));
}

export function useReportingByCampaign(q: ReportingQuery = {}, enabled = true) {
  return useQuery(reportingByCampaign(q, { enabled }));
}

export function useReportingByProduction(q: ReportingQuery = {}, enabled = true) {
  return useQuery(reportingByProduction(q, { enabled }));
}

export function useReportingByChannel(q: ReportingQuery = {}, enabled = true) {
  return useQuery(reportingByChannel(q, { enabled }));
}

export function useReportingByAudience(q: ReportingQuery = {}, enabled = true) {
  return useQuery(reportingByAudience(q, { enabled }));
}

export function useReportingByAudienceType(q: ReportingQuery = {}, enabled = true) {
  return useQuery(reportingByAudienceType(q, { enabled }));
}

export function useReportingByCreative(q: ReportingQuery = {}, enabled = true) {
  return useQuery(reportingByCreative(q, { enabled }));
}

export function useReportingQuery(request: ReportQueryRequest, enabled = true) {
  return useQuery(reportingQuery(request, { enabled }));
}

/**
 * Resolve every bounded page for dimension pickers. The reporting API caps a
 * response at 500 rows but its canonical source aggregation is bounded at
 * 5,000, so ten pages are sufficient; the extra guard fails closed if either
 * bound changes without this client being updated.
 */
export function useReportingQueryAll(request: ReportQueryRequest, enabled = true) {
  return useQuery(reportingQueryAllPages(request, { enabled }));
}

export function useReportingExport() {
  return useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (request: ReportQueryRequest) =>
      api.post<string>('/reporting/export', { body: request }),
    retry: 0,
  });
}

/** Client-ready, assigned-production-only Performance export. */
export function usePerformanceReportExport() {
  return useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (request: ReportQueryRequest) =>
      api.post<string>('/reporting/performance-export', { body: request }),
    retry: 0,
  });
}

/** Client-ready total/direct/halo CSV for every Perf + Halo tab. */
export function usePerfHaloReportExport() {
  return useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (request: PerfHaloExportRequest) =>
      api.post<string>('/reporting/perf-halo-export', { body: request }),
    retry: 0,
  });
}

// ── Markup ─────────────────────────────────────────────────────────────────

export function useMarkupSummary(q: MarkupQuery = {}) {
  return useQuery(markupSummary(q));
}

export function useMarkupByCampaign(q: MarkupQuery = {}) {
  return useQuery(markupByCampaign(q));
}

export function useMarkupByChannel(q: MarkupQuery = {}) {
  return useQuery(markupByChannel(q));
}

export function useMarkupByProduction(q: MarkupQuery = {}) {
  return useQuery(markupByProduction(q));
}

// ── Delivery ───────────────────────────────────────────────────────────────

export function useDeliveryPlacements(q: DeliveryQuery = {}) {
  return useQuery(deliveryPlacements(q));
}

// ── Spillover ──────────────────────────────────────────────────────────────

export function useSpilloverSummary(q: SpilloverQuery = {}) {
  return useQuery(spilloverSummary(q));
}

export function useSpilloverFlows(q: SpilloverQuery = {}) {
  return useQuery(spilloverFlows(q));
}

export function useSpilloverTopHalo(q: SpilloverQuery = {}) {
  return useQuery(spilloverTopHalo(q));
}

// ── Admin ──────────────────────────────────────────────────────────────────

export function useAdminUsers(q: AdminUsersQuery = {}) {
  return useQuery(adminUsers(q));
}

export function useAdminUser(id: string) {
  return useQuery(adminUserById(id, { enabled: !!id }));
}

export function useAdminRoles() {
  return useQuery(adminRoles(undefined));
}

export function useAuditEvents(q: AuditQuery = {}) {
  return useQuery(adminAudit(q));
}

/** The newest `limit` audit events for a query, in one request. */
export function useRecentAuditEvents(q: AuditQuery, limit: number) {
  return useQuery(adminAuditRecent({ ...q, limit }));
}

// ── Realtime (polled) ──────────────────────────────────────────────────────


export function usePipelineMonitorPolicy() {
  return useQuery(pipelinePolicy(undefined));
}

// Unified ingest-run log (D1 `ingest_runs`) — scheduled refresh/backfill + manual.
// Bare array; poll it in realtime so the Runs drawer shows live progress + quality.
// `pollMs` lets ambient consumers (the chrome health chip) poll slowly; when the
// pipeline page is also mounted TanStack uses the smallest active interval.
export function useIngestRuns(pollMs: number = CADENCE.fast) {
  return useQuery(pipelineRuns(undefined, { cadence: pollMs }));
}

// Per-production gap-fix corrections (bare array), for the drawer corrections panel.
export function usePipelineCorrections(productionId: string) {
  return useQuery(pipelineCorrections(productionId, { enabled: !!productionId }));
}

// Productions-driven pipeline monitor: per-production 30-day aggregate (bare array).
export function usePipelineStats() {
  return useQuery(pipelineStats(undefined));
}

// Per-production daily series (bare array), for the detail charts + daily table.
export function usePipelineStatDetail(productionId: string) {
  return useQuery(pipelineStatDetail(productionId, { enabled: !!productionId }));
}

// Per-(pixel, event_type) web-event counts (1h/24h/7d + newest event) from
// `web_events` via the batch tunnel. 60s poll matches the server-side KV TTL —
// activity is a freshness signal, not a 5s dashboard.

// `enabled: false` for callers whose role lacks pixels_view (the endpoint 403s).
export function usePixelActivity(enabled: boolean = true) {
  return useQuery(pixelActivity(undefined, { enabled }));
}

// Single authoritative health read (D1 heartbeat + tunnel-backed error rates),
// composed server-side into one HealthStatus per component so the navbar chip,
// Health tiles, and alert engine all mean the same "green". 60s ambient poll.

export function useHealthSummary() {
  return useQuery(healthSummary(undefined));
}

/// System-metric bands (the unified quality thresholds) — feeds the navbar pill,
/// the System Health page, and every de-hardcoded screen cell. Same 60s cadence
/// as the health summary; `enabled` lets non-backend roles skip it (endpoint 403s).
export function useSystemHealth(enabled: boolean = true) {
  return useQuery(healthMetrics(undefined, { enabled }));
}

// Conversion delivery (CAPI Forwarding) health — queue depth, per-target
// summary/coverage/recent feed + delivery-job history, composed server-side by
// the batch tunnel. 30s poll: a delivery cycle is minutes-scale, not seconds.

export function useConversionsHealth() {
  return useQuery(conversionsHealth(undefined));
}

// ── Data Pipeline (flow) ───────────────────────────────────────────────────
// Contract: GET /flow/snapshot (10s), GET /flow/live (5s full pages — the
// server caps at the 200 newest; paused ⇒ no polling), POST
// /flow/reveal|/flow/tickets-reveal (admin, audited),
// GET /flow/tickets-recent, GET /flow/history/* (on-demand cached).

export function useFlowSnapshot(productionId: string | null) {
  return useQuery(flowSnapshot(productionId));
}

export interface FlowFeedFilters {
  /**
   * Production scope — sent to /flow/live (the server filters the page) and
   * applied again client-side on redacted.production_id.
   */
  productionId?: string | null;
  channel?: 'all' | 'web' | 'server';
  eventType?: string;
  pixelId?: string;
}

interface FeedAccum {
  /** The production scope these events were read under (null = all). */
  scope: string | null;
  events: FlowEventRedacted[];
  newSeqs: ReadonlySet<number>;
}

/**
 * Live tap feed: polls /flow/live every 5s for FULL pages (the server caps at
 * the 200 newest — no incremental cursor: a cursor wedges after a tap restart
 * resets the seq space, and never re-serves rows mutated server-side, e.g.
 * mark_resolved flipping person_id_resolved on already-served events).
 * Pages apply via `feedAfterPage`: a `started_at` change replaces the whole
 * list (buffer restart), otherwise replace-by-seq merge. `paused` freezes the
 * poll entirely (refetchInterval false) AND drops any page that still lands
 * (an in-flight response completing after Pause, a staleness refetch) — the
 * frozen list stays byte-stable until resume, when the next full-page merge
 * catches everything the freeze skipped.
 */
export function useFlowFeed(filters: FlowFeedFilters, paused: boolean, enabled: boolean = true) {
  const scope = filters.productionId ?? null;
  // The previous page's `started_at`, per scope: a scope change is a new
  // stream, never a "restart" of the old one.
  const startedAtRef = useRef<{ scope: string | null; startedAt: string | null }>({ scope, startedAt: null });
  const [feed, setFeed] = useState<FeedAccum>(() => ({ scope, events: [], newSeqs: new Set<number>() }));

  // Pause is a property of this screen's UI state, not of the read, so it is a
  // call-site cadence override rather than a field on the resource.
  const query = useQuery(flowFeed(scope, { enabled, cadence: paused ? false : undefined }));

  const page = query.data;
  useEffect(() => {
    // §6.1 freeze / §6.4 privacy affordance: while paused the frozen list
    // must not mutate — drop the page. The started_at ref is not advanced
    // either, so a buffer restart during the freeze is still detected on
    // resume (this effect re-runs when `paused` flips and merges the last
    // cached page; the next poll completes the catch-up).
    if (paused || !page) return;
    const prevStartedAt = startedAtRef.current.scope === scope ? startedAtRef.current.startedAt : null;
    startedAtRef.current = { scope, startedAt: page.started_at };
    setFeed((prior) => {
      // Events read under another production scope never carry over.
      const prev = prior.scope === scope ? prior : { scope, events: [], newSeqs: new Set<number>() };
      const restarted = prevStartedAt != null && page.started_at !== prevStartedAt;
      const events = feedAfterPage(prev.events, page, false, prevStartedAt);
      // Accent-flash only genuinely NEW rows — full pages re-serve everything,
      // and replaced copies (person flips) must not flash as arrivals.
      const known = new Set(prev.events.map((e) => Number(e.seq)));
      const fresh = new Set(
        page.events.map((e) => Number(e.seq)).filter((s) => restarted || !known.has(s)),
      );
      if (prev === prior && events === prev.events && fresh.size === 0) return prior; // no-op tick
      return { scope, events, newSeqs: fresh.size ? fresh : prev.newSeqs };
    });
  }, [page, paused, scope]);

  const allEvents = useMemo(() => (feed.scope === scope ? feed.events : []), [feed, scope]);
  const newSeqs = feed.newSeqs;
  // No production predicate here: the server already scoped the page to the
  // production (`/flow/live?production_id=`, matched there with its aliases), and
  // a second, exact-spelling check in the browser dropped the whole page whenever
  // an event spelled the id differently. A scope change still resets the feed
  // above (`prior.scope !== scope`).
  const { channel, eventType, pixelId } = filters;
  const events = useMemo(
    () =>
      allEvents.filter(
        (e) =>
          (channel == null || channel === 'all'
            || (channel === 'web' ? e.channel === 'pixel' : e.channel === 'pixel_sgtm'))
          && (eventType == null || eventType === 'all' || isEventType(e.event_type, eventType))
          && (pixelId == null || pixelId === 'all' || e.pixel_id === pixelId),
      ),
    [allEvents, channel, eventType, pixelId],
  );

  return { ...query, page, events, allEvents, newSeqs };
}

export type FlowHistoryGranularity = 'auto' | 'hour' | 'day';

export interface FlowHistoryQuery {
  /** RFC3339 range bounds. */
  from: string;
  to: string;
  granularity: FlowHistoryGranularity;
  productionId?: string | null;
  pixelId?: string | null;
}

/**
 * The three HISTORY reads for one range/scope. Pass `null` to keep the
 * queries dormant (LIVE mode). Responses cache per exact query.
 */
export function useFlowHistory(q: FlowHistoryQuery | null) {
  // A disabled query still needs a well-formed key, so an absent `q` supplies
  // an empty one; `enabled: !!q` is what stops it firing.
  const effective = q ?? { from: '', to: '', granularity: '' };
  const web = useQuery(flowHistoryWebEvents(effective, { enabled: !!q }));
  const tickets = useQuery(flowHistoryTickets(effective, { enabled: !!q }));
  const conversions = useQuery(flowHistoryConversions(effective, { enabled: !!q }));
  // Combined object memoized on the three sub-`.data` identities — react-query
  // structural sharing keeps those stable across no-op refetches, so consumers
  // can key derived memos/effects on `data` identity. A fresh literal here
  // re-minted every downstream derivation on each parent render (the pipeline
  // page re-renders on a 5s clock), which force-closed the chart-explain dock.
  const data = useMemo(
    () => (web.data && tickets.data && conversions.data
      ? { web: web.data, tickets: tickets.data, conv: conversions.data }
      : undefined),
    [web.data, tickets.data, conversions.data],
  );
  return {
    web, tickets, conversions, data,
    isLoading: web.isLoading || tickets.isLoading || conversions.isLoading,
    isError: web.isError || tickets.isError || conversions.isError,
    error: web.error ?? tickets.error ?? conversions.error,
    refetch: () => { void web.refetch(); void tickets.refetch(); void conversions.refetch(); },
  };
}

/** Redacted recent tickets for the encore context panel (fetch on select). */
export function useFlowTicketsRecent(productionId: string | null, enabled: boolean) {
  return useQuery(flowTicketsRecent(productionId, { enabled }));
}

/**
 * E2E per-stage lineage for one correlation_id (TRACE mode). Enabled only when
 * an id is active. Polls while the item is still in flight (no terminal
 * destination send yet) so later stages fill in; stops once terminal.
 */
export function useFlowLineage(correlationId: string | null) {
  return useQuery(flowLineage(correlationId, { enabled: !!correlationId }));
}

/**
 * Resolve a bare UUID that could be a ticket_id OR a web_event_id (both are
 * UUIDs) by probing BOTH lineage lanes. Returns which lane(s) hold any
 * evidence of it. Reuses the exact `useFlowLineage` query key/fn, so the winning lane's
 * rows are already warm when the caller navigates to it.
 */
export function useResolveBareTrace() {
  const qc = useQueryClient();
  return useCallback(
    async (uuid: string): Promise<{ ticket: boolean; web: boolean }> => {
      const probe = async (cid: string): Promise<boolean> => {
        try {
          // The resource's own options object, not a re-typed copy of it. This
          // used to spell out the key, the URL and a `staleTime` by hand right
          // next to `flowLineage`, so a change to the resource's request would
          // have written a DIFFERENT payload into the same cache slot the
          // lineage screen reads — warm, wrong, and silent. The 5-second
          // staleTime went with it: the two probes use different keys, so it
          // only ever helped someone pasting the same uuid twice, and freshness
          // is the resource's call.
          const resp = await qc.fetchQuery(flowLineage(cid));
          // Evidence is any of the three reads: an observation, a queue row, or
          // (ticket lane) the ticket as stored. A ticket the queue still holds
          // with no retained observation is found, not missing.
          return (
            (resp.rows ?? []).length > 0
            || (resp.destinations ?? []).length > 0
            || resp.ticket_lookup === 'found'
            // Too many lines to read whole still proves the ticket exists.
            || resp.ticket_lookup === 'incomplete'
          );
        } catch {
          return false;
        }
      };
      const [ticket, web] = await Promise.all([probe(`tkt:${uuid}`), probe(`web:${uuid}`)]);
      return { ticket, web };
    },
    [qc],
  );
}

// Admin PII reveals — audited server-side. retry 0 (a retry would double-log
// the audit event). The caller copies the response into LOCAL component state
// and resets the mutation; the payload never enters the query cache.
export function useRevealFlowEvent() {
  return useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (seq: number) => api.post<FlowRevealResponse>('/flow/reveal', { body: { seq } }),
    retry: 0,
  });
}

export function useRevealTicket() {
  return useRegisteredMutation({
    noDataEffect: true,
    mutationFn: (ticketId: string) =>
      api.post<TicketRevealResponse>('/flow/tickets-reveal', { body: { ticket_id: ticketId } }),
    retry: 0,
  });
}

// ── Pipeline Copilot ───────────────────────────────────────────────────────
// Contract: GET /copilot/status (the SINGLE feature gate + Daily Briefing
// source, read live; it says whether a briefing is being written and how the
// last run ended), POST /copilot/explain[?force=true] (2–4s, answered whole —
// no streaming), POST /copilot/brief/regenerate (202: a briefing run started,
// or one was already running; the briefing itself lands on the status). Every
// affordance in the app gates on `status.enabled` — no status data ⇒ nothing
// copilot renders.

// The precedent this whole registry generalises: one options object shared
// between a hook and a route guard. It is now an ordinary resource.
export const copilotStatusQueryOptions = copilotStatus(undefined);

export function useCopilotStatus() {
  return useQuery(copilotStatus(undefined));
}

export interface ExplainVars {
  request: ExplainRequest;
  /** Regenerate: forces a fresh generation, dropping the server cache. */
  force?: boolean;
}

// retry 0: a 2–4s generation must not silently double on failure; the panel
// surfaces the error with an explicit Retry instead.
export function useExplain() {
  return useRegisteredMutation({
    noDataEffect: true,
    mutationFn: ({ request, force }: ExplainVars) =>
      api.post<ExplainResponse>(`/copilot/explain${force ? '?force=true' : ''}`, { body: request }),
    retry: 0,
  });
}

export function useRegenerateBriefing() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    // `copilot.status` is volatile (no data revision moves for it), so naming
    // it here is the one registered path that refetches it. The
    // mutation-usage guard checks the name resolves to that volatile resource.
    volatileResources: ['copilot.status'],
    mutationFn: (_: void) => api.post<RegenerateStarted>('/copilot/brief/regenerate'),
    retry: 0,
    onSuccess: async (started) => {
      // The answer is a 202: the briefing is written in the background and
      // lands on the status. Keep the briefing on screen and mark the status
      // as writing, so its payload cadence starts polling at once; the
      // registered refetch that follows replaces this guess with the server's
      // own `generating`, and only the server's `false` stops the poll. A poll
      // already in flight is cancelled first, or its answer from before the
      // start would overwrite the mark and stop the watch.
      await qc.cancelQueries({ queryKey: copilotStatus.key() });
      qc.setQueryData<CopilotStatus>(copilotStatus.key(), (prev) =>
        prev
          ? { ...prev, generating: true, generating_since: started.generating_since ?? prev.generating_since }
          : prev);
    },
  });
}

export function useAdminLogs(filters: LogFilters = {}) {
  return useQuery(adminLogs(filters));
}

export function useLogStats(windowHours: number = 24) {
  return useQuery(adminLogStats(windowHours));
}

export function useLogGroups(windowHours: number = 168, level?: string) {
  return useQuery(adminLogGroups({ windowHours, level: level ?? null }));
}

export function useAlerts(activeOnly: boolean = false, enabled: boolean = true) {
  return useQuery(adminAlerts(activeOnly, { enabled }));
}

export function useAlertById(id: string | null | undefined) {
  return useQuery(adminAlertById(id ?? '', { enabled: !!id }));
}

export function useAlertRules(enabled: boolean = true) {
  return useQuery(adminAlertRules(undefined, { enabled }));
}

export function useNotificationChannels(enabled: boolean = true) {
  return useQuery(adminNotificationChannels(undefined, { enabled }));
}

// Cold archive (R2 NDJSON). Immutable objects — no polling.
export function useLogArchive(date: string, hour?: string, enabled: boolean = true) {
  return useQuery(adminLogArchive({ date, hour: hour ?? null }, { enabled: enabled && !!date }));
}

export function useLogArchiveObject(key: string | null) {
  return useQuery(adminLogArchiveObject(key ?? '', { enabled: !!key }));
}

// ── Helpers ────────────────────────────────────────────────────────────────


// Keep the established hooks module as the public barrel while the Manage
// write/status surface stays isolated in a focused implementation module.
export * from './manage-hooks';
